/**
 * SignatureCaptureRouter — picks the right capture primitive per Q1 tiering.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Centralises the click/typed/drawn dispatch so the SurfaceDispatcher
 * (Wave 2) only needs to know *which mode* a document requires, not which
 * of three React components to mount. When new capture modes ship (e.g.
 * a future biometric one), only this router gains a case.
 *
 * The router is intentionally dumb — no state, no API calls, no consent
 * checks. ESIGN consent is the EsignConsentGate's responsibility; this
 * component fires once consent has already passed.
 */

import type { CSSProperties } from 'react';
import { ClickToSignCapture } from './ClickToSignCapture';
import { TypedSignatureCapture } from './TypedSignatureCapture';
import { DrawnSignatureCapture } from './DrawnSignatureCapture';
import { DisclosureReviewCapture } from './DisclosureReviewCapture';
import type {
  SignatureCaptureMode,
  SignatureCaptureResult,
} from '@retail-ops/shared-types';

// ── Props ──────────────────────────────────────────────────────────────────

export interface SignatureCaptureRouterProps {
  /** Q1 doc-role tier driving which primitive renders. */
  mode: SignatureCaptureMode;
  /** Full display name — used by 'click' and 'drawn' primitives. */
  signerName: string;
  /**
   * Required when `mode === 'typed'`. Used to pre-fill the customer's
   * typed-name input.
   */
  signerFirstName?: string;
  /** Required when `mode === 'typed'`. */
  signerLastName?: string;
  /** Document title shown in the headline copy. */
  documentTitle: string;
  /** Pass-through to DrawnSignatureCapture's mobile-fallback flag. */
  isTouchDevice?: boolean;
  /** Capture result handler — invoked by whichever primitive renders. */
  onCapture: (result: SignatureCaptureResult) => void;
  /** Cancel handler — invoked when the customer dismisses. */
  onCancel: () => void;
  /** When true, the underlying primitive renders disabled (mid-submit). */
  disabled?: boolean;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const wrapS: CSSProperties = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
};

const errorS: CSSProperties = {
  padding: 16,
  background: '#fef2f2',
  color: '#991b1b',
  border: '1px solid #fecaca',
  borderRadius: 8,
  maxWidth: 520,
  width: '100%',
  fontSize: 13,
  lineHeight: 1.5,
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

// ── Component ──────────────────────────────────────────────────────────────

export function SignatureCaptureRouter(
  props: SignatureCaptureRouterProps
): JSX.Element {
  const {
    mode,
    signerName,
    signerFirstName,
    signerLastName,
    documentTitle,
    isTouchDevice,
    onCapture,
    onCancel,
    disabled,
  } = props;

  switch (mode) {
    case 'click':
      return (
        <div style={wrapS}>
          <ClickToSignCapture
            signerName={signerName}
            documentTitle={documentTitle}
            onCapture={onCapture}
            onCancel={onCancel}
            disabled={disabled}
          />
        </div>
      );

    case 'typed': {
      // Fall back to splitting the display name on whitespace when the
      // caller hasn't supplied first/last separately — keeps the router
      // resilient against partial signer-record data.
      const [fallbackFirst, ...fallbackRest] = signerName.trim().split(/\s+/);
      const fallbackLast = fallbackRest.join(' ');
      return (
        <div style={wrapS}>
          <TypedSignatureCapture
            signerFirstName={signerFirstName ?? fallbackFirst ?? ''}
            signerLastName={signerLastName ?? fallbackLast}
            documentTitle={documentTitle}
            onCapture={onCapture}
            onCancel={onCancel}
            disabled={disabled}
          />
        </div>
      );
    }

    case 'drawn':
      return (
        <div style={wrapS}>
          <DrawnSignatureCapture
            signerName={signerName}
            documentTitle={documentTitle}
            onCapture={onCapture}
            onCancel={onCancel}
            disabled={disabled}
            isTouchOnly={isTouchDevice}
          />
        </div>
      );

    case 'review':
      // A view-only disclosure (Bill 2026-09-30): presented + acknowledged, no signature.
      return (
        <div style={wrapS}>
          <DisclosureReviewCapture
            signerName={signerName}
            documentTitle={documentTitle}
            onCapture={onCapture}
            onCancel={onCancel}
            disabled={disabled}
          />
        </div>
      );

    default: {
      // Exhaustiveness check — TS will flag this branch if a new mode is
      // added to the union but not wired here. Run-time fallback renders
      // a banner so a misconfigured doc-row doesn't silently no-op.
      const _exhaustive: never = mode;
      return (
        <div style={wrapS}>
          <div role="alert" style={errorS}>
            We could not load the signature capture for this document. The
            requested mode <code>{String(_exhaustive)}</code> is not supported
            by this version of the app — please contact a Personal Banker.
          </div>
        </div>
      );
    }
  }
}

export default SignatureCaptureRouter;
